import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView } from "framer-motion";
import emailjs from "@emailjs/browser";
import "../styles/HomePage.css";

// Optimized Particle System Component with React.memo
const ParticleSystem = memo(({ count = 50 }) => {
  const [particles, setParticles] = useState([]);
  const containerRef = useRef();

  // Memoize initial particles generation
  const initialParticles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      speed: Math.random() * 0.5 + 0.1,
      direction: Math.random() * Math.PI * 2,
      opacity: Math.random() * 0.5 + 0.1,
    })), [count]);

  useEffect(() => {
    setParticles(initialParticles);
  }, [initialParticles]);

  // Optimize animation loop with requestAnimationFrame
  useEffect(() => {
    let animationId;
    let lastTime = 0;
    const targetFPS = 30; // Reduce FPS for better performance
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime) => {
      if (currentTime - lastTime >= frameInterval) {
        setParticles(prev => prev.map(particle => ({
          ...particle,
          x: (particle.x + Math.cos(particle.direction) * particle.speed) % 100,
          y: (particle.y + Math.sin(particle.direction) * particle.speed) % 100,
          direction: particle.direction + (Math.random() - 0.5) * 0.1,
        })));
        lastTime = currentTime;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Memoize particle animation props
  const getParticleAnimation = useCallback((particle) => ({
    scale: [1, 1.5, 1],
    opacity: [particle.opacity, particle.opacity * 1.5, particle.opacity],
  }), []);

  const getParticleTransition = useCallback((particle) => ({
    duration: 3 + (particle.id % 3),
    repeat: Infinity,
    ease: "easeInOut",
  }), []);

  return (
    <div className="particle-system" ref={containerRef} role="presentation" aria-hidden="true">
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="particle"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            willChange: 'transform',
          }}
          animate={getParticleAnimation(particle)}
          transition={getParticleTransition(particle)}
        />
      ))}
    </div>
  );
});

// Optimized Morphing Logo Component
const MorphingLogo = memo(({ src, alt, className }) => {
  const [morphPhase, setMorphPhase] = useState(0);
  const logoRef = useRef();
  const animationRef = useRef();
  const isInView = useInView(logoRef, { once: false, margin: "-100px" });

  useEffect(() => {
    if (!isInView) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }
    
    let lastTime = 0;
    const targetFPS = 30; // Reduce FPS for better performance
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime) => {
      if (currentTime - lastTime >= frameInterval) {
        setMorphPhase(prev => (prev + 0.05) % (Math.PI * 2));
        lastTime = currentTime;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isInView]);

  // Memoize expensive calculations
  const morphTransform = useMemo(() => `
    perspective(1000px)
    rotateY(${Math.sin(morphPhase) * 10}deg)
    rotateX(${Math.cos(morphPhase * 0.7) * 5}deg)
    scale(${1 + Math.sin(morphPhase * 1.3) * 0.05})
  `, [morphPhase]);

  const filterStyle = useMemo(() => 
    `hue-rotate(${Math.sin(morphPhase) * 30}deg) brightness(${1 + Math.sin(morphPhase * 2) * 0.1})`, 
    [morphPhase]
  );

  const hoverAnimation = useMemo(() => ({
    scale: 1.1,
    rotateY: 15,
    transition: { duration: 0.3 }
  }), []);

  return (
    <motion.div
      ref={logoRef}
      className={className}
      style={{
        transform: morphTransform,
        filter: filterStyle,
        willChange: 'transform, filter',
      }}
      whileHover={hoverAnimation}
      role="img"
      aria-label={alt}
    >
      <img 
        src={src} 
        alt={alt}
        loading="lazy"
        decoding="async"
        style={{ willChange: 'transform' }}
      />
    </motion.div>
  );
});

// Optimized Geometric Background Component
const GeometricBackground = memo(() => {
  const { scrollYProgress } = useScroll();
  
  // Memoize shapes generation to prevent recreation on every render
  const shapes = useMemo(() => 
    Array.from({ length: 15 }, (_, i) => ({ // Reduced count for performance
      id: i,
      type: ['circle', 'triangle', 'square'][i % 3], // More predictable pattern
      x: (i % 5) * 20 + Math.random() * 10, // More structured positioning
      y: Math.floor(i / 5) * 33 + Math.random() * 10,
      size: 60 + (i % 3) * 20, // More consistent sizing
      rotation: i * 45,
      opacity: 0.03 + (i % 3) * 0.02,
      speed: 0.2 + (i % 3) * 0.1,
    })), []
  );

  const rotateShapes = useTransform(scrollYProgress, [0, 1], [0, 180]); // Reduced rotation

  // Memoize animation props
  const getShapeAnimation = useCallback((shape) => ({
    rotate: rotateShapes,
    x: [0, Math.sin(shape.id * 0.5) * 30, 0], // Reduced movement
    y: [0, Math.cos(shape.id * 0.5) * 20, 0],
  }), [rotateShapes]);

  const getShapeTransition = useCallback((shape) => ({
    duration: 15 + shape.id * 2, // Slower animations
    repeat: Infinity,
    ease: "linear",
  }), []);

  return (
    <div className="geometric-background" role="presentation" aria-hidden="true">
      {shapes.map(shape => (
        <motion.div
          key={shape.id}
          className={`geometric-shape ${shape.type}`}
          style={{
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            width: `${shape.size}px`,
            height: `${shape.size}px`,
            opacity: shape.opacity,
            willChange: 'transform',
          }}
          animate={getShapeAnimation(shape)}
          transition={getShapeTransition(shape)}
        />
      ))}
    </div>
  );
});

// Complex Geometric Design for Hero Logo Background
const ComplexGeometricDesign = memo(() => {
  const [time, setTime] = useState(0);
  const containerRef = useRef();
  const animationRef = useRef();
  const lastUpdateRef = useRef(0);
  const positionCacheRef = useRef(new Map()); // Cache for position calculations
  const isInView = useInView(containerRef, { once: false, margin: "-50px" });

  // Optimized time-based animation loop with adaptive frame rate
  useEffect(() => {
    // Capture ref values at the beginning of the effect
    const animationRefCurrent = animationRef.current;
    const positionCache = positionCacheRef.current;
    
    if (!isInView) {
      if (animationRefCurrent) {
        cancelAnimationFrame(animationRefCurrent);
      }
      return;
    }

    let lastTime = 0;
    const targetFPS = 30; // Reduced from 60 to 30 for better performance
    const frameInterval = 1000 / targetFPS;
    const timeIncrement = 0.015; // Slightly reduced for smoother animation

    const animate = (currentTime) => {
      if (currentTime - lastTime >= frameInterval) {
        // Only update if enough time has passed to avoid unnecessary re-renders
        const timeDelta = currentTime - lastUpdateRef.current;
        if (timeDelta >= 16) { // ~60fps limit for state updates
          setTime(prev => prev + timeIncrement);
          lastUpdateRef.current = currentTime;
        }
        lastTime = currentTime;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Clear position cache on cleanup to prevent memory leaks
      if (positionCache) {
        positionCache.clear();
      }
    };
  }, [isInView]);

  // Generate simplified geometric patterns (drastically reduced for performance)
  const geometricElements = useMemo(() => {
    const elements = [];

    // Simple rotating ring with just 6 nodes total
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      elements.push({
        id: `ring-${i}`,
        type: 'ring-node',
        baseRadius: 100,
        baseAngle: angle,
        ring: 0,
        index: i,
        size: 5,
        opacity: 0.4,
        rotationSpeed: 0.2,
        direction: 1,
      });
    }

    // Just 2 orbital particles
    for (let orbit = 0; orbit < 2; orbit++) {
      elements.push({
        id: `orbit-${orbit}`,
        type: 'orbital-particle',
        baseRadius: 70 + orbit * 30,
        speed: 0.3 + orbit * 0.2,
        orbit: orbit,
        size: 4,
        opacity: 0.5,
      });
    }

    // Just 4 connecting lines
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      elements.push({
        id: `connector-${i}`,
        type: 'connector-line',
        angle: angle,
        index: i,
        opacity: 0.15,
      });
    }

    return elements;
  }, []);

  // Highly optimized position calculation with memoization and reduced object creation
  const getElementPosition = useCallback((element) => {
    const cacheKey = `${element.id}-${Math.floor(time * 20)}`; // Cache based on rounded time
    
    // Check cache first
    if (positionCacheRef.current.has(cacheKey)) {
      return positionCacheRef.current.get(cacheKey);
    }
    
    const centerX = 175;
    const centerY = 175;
    let result;
    
    switch (element.type) {
      case 'ring-node': {
        // Use pre-calculated rotation speed and direction
        const currentAngle = element.baseAngle + time * element.rotationSpeed * element.direction;
        const radiusVariation = Math.sin(time * 1.8 + element.ring) * 4;
        const currentRadius = element.baseRadius + radiusVariation;
        
        // Cache trigonometric calculations
        const cosAngle = Math.cos(currentAngle);
        const sinAngle = Math.sin(currentAngle);
        
        result = {
          x: centerX + cosAngle * currentRadius,
          y: centerY + sinAngle * currentRadius,
          scale: 1 + Math.sin(time * 2.5 + element.index) * 0.2,
          opacity: element.opacity * (0.6 + Math.sin(time * 1.8 + element.ring) * 0.4),
        };
        break;
      }
      
      case 'spiral-node': {
        const currentAngle = element.baseAngle + time * 0.18;
        const breathingRadius = element.baseRadius + Math.sin(time * 1.3 + element.progressOffset) * 8;
        
        // Cache trigonometric calculations
        const cosAngle = Math.cos(currentAngle);
        const sinAngle = Math.sin(currentAngle);
        
        result = {
          x: centerX + cosAngle * breathingRadius,
          y: centerY + sinAngle * breathingRadius,
          scale: 1 + Math.sin(time * 3.2 + element.progressOffset) * 0.3,
          opacity: element.opacity * (0.4 + Math.sin(time + element.armOffset) * 0.6),
        };
        break;
      }
      
      case 'orbital-particle': {
        const currentAngle = time * element.speed;
        const wobble = Math.sin(time * 2.5 + element.orbit) * 6;
        const currentRadius = element.baseRadius + wobble;
        
        // Cache trigonometric calculations
        const cosAngle = Math.cos(currentAngle);
        const sinAngle = Math.sin(currentAngle);
        
        result = {
          x: centerX + cosAngle * currentRadius,
          y: centerY + sinAngle * currentRadius,
          scale: 1 + Math.sin(time * 4 + element.orbit) * 0.3,
          opacity: element.opacity * (0.5 + Math.sin(time * 1.6 + element.orbit) * 0.5),
        };
        break;
      }
      
      default:
        result = { x: centerX, y: centerY, scale: 1, opacity: element.opacity };
    }
    
    // Cache the result and clean old entries
    positionCacheRef.current.set(cacheKey, result);
    if (positionCacheRef.current.size > 100) {
      const firstKey = positionCacheRef.current.keys().next().value;
      positionCacheRef.current.delete(firstKey);
    }
    
    return result;
  }, [time]);

  return (
    <div className="complex-geometric-design" ref={containerRef} role="presentation" aria-hidden="true">
      {/* SVG for connecting lines */}
      <svg className="geometric-connections" viewBox="0 0 350 350">
        <defs>
          <radialGradient id="connectionGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(102, 126, 234, 0.4)" />
            <stop offset="50%" stopColor="rgba(240, 147, 251, 0.2)" />
            <stop offset="100%" stopColor="rgba(79, 172, 254, 0.1)" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Optimized connecting web */}
        {geometricElements.filter(el => el.type === 'connector-line').map(connector => {
          // Slower, more efficient rotations
          const angle1 = connector.angle + time * 0.07;
          const angle2 = connector.angle + Math.PI + time * 0.1;
          const radius1 = 65 + Math.sin(time * 0.8 + connector.index) * 15; // Reduced variation
          const radius2 = 115 + Math.cos(time * 0.9 + connector.index) * 20; // Reduced variation
          
          // Cache trigonometric calculations
          const cos1 = Math.cos(angle1);
          const sin1 = Math.sin(angle1);
          const cos2 = Math.cos(angle2);
          const sin2 = Math.sin(angle2);
          
          const x1 = 175 + cos1 * radius1;
          const y1 = 175 + sin1 * radius1;
          const x2 = 175 + cos2 * radius2;
          const y2 = 175 + sin2 * radius2;
          
          return (
            <motion.line
              key={connector.id}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="url(#connectionGradient)"
              strokeWidth="1"
              filter="url(#glow)"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: connector.opacity * (0.4 + Math.sin(time * 1.5 + connector.index) * 0.6),
                strokeWidth: 1 + Math.sin(time * 2.2 + connector.index) * 0.3, // Reduced stroke variation
              }}
              transition={{ duration: 0.15 }} // Slightly longer transition
            />
          );
        })}
        
        {/* Central pulsing circle */}
        <motion.circle
          cx="175"
          cy="175"
          r={15 + Math.sin(time * 2) * 5}
          fill="none"
          stroke="rgba(102, 126, 234, 0.3)"
          strokeWidth="2"
          filter="url(#glow)"
          animate={{
            opacity: [0.2, 0.8, 0.2],
            strokeWidth: [1, 3, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </svg>
      
      {/* Geometric nodes */}
      {geometricElements.filter(el => el.type !== 'connector-line').map(element => {
        const position = getElementPosition(element);
        
        return (
          <motion.div
            key={element.id}
            className={`geometric-node ${element.type}`}
            style={{
              left: position.x,
              top: position.y,
              width: `${element.size}px`,
              height: `${element.size}px`,
              willChange: 'transform, opacity',
            }}
            animate={{
              scale: position.scale,
              opacity: position.opacity,
            }}
            transition={{
              duration: 0.1,
              ease: "linear"
            }}
          />
        );
      })}
      
      {/* Hexagonal grid removed for performance */}
    </div>
  );
});

export default function HomePage() {
  const formRef = useRef();
  const containerRef = useRef();
  const { scrollYProgress } = useScroll({ target: containerRef });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  
  // Advanced scroll-based transforms
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);
  
  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Optimized mouse tracking with throttling
  useEffect(() => {
    if (isMobile) return;
    
    let ticking = false;
    
    const handleMouseMove = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setMousePosition({
            x: (e.clientX / window.innerWidth) * 2 - 1,
            y: (e.clientY / window.innerHeight) * 2 - 1,
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  // Memoized form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  }, [errors]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      
      if (result.text === 'OK') {
        setSubmitStatus('success');
        setFormData({
          name: "",
          email: "",
          company: "",
          message: ""
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm]);

  // Memoized service data to prevent recreation
  const servicesData = useMemo(() => [
    {
      title: "Application Development",
      image: "/computercoffee.jpeg",
      description: "We craft bespoke applications that perfectly align with your business objectives. Our development process combines cutting-edge technologies with industry best practices to deliver scalable, secure, and user-friendly solutions.",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      delay: 0
    },
    {
      title: "AI Integration",
      image: "/handcloud.jpeg", 
      description: "Transform your products with state-of-the-art artificial intelligence capabilities. Our AI integration services span machine learning, natural language processing, computer vision, and predictive analytics.",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      delay: 0.2
    },
    {
      title: "Blockchain & Crypto Development",
      image: "/canadamotherboard.jpeg",
      description: "Build the future of decentralized applications with our blockchain expertise. We specialize in Solana development, smart contracts, DeFi protocols, NFT platforms, and Web3 integrations that push the boundaries of what's possible in the crypto space.",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      delay: 0.6
    }
  ], []);

  // Memoized about data with expanded content
  const aboutData = useMemo(() => [
    {
      title: "Innovation at Core",
      icon: "/hayden-bulk-transparent.png",
      description: "Based in Toronto, Canada, Hayden Bulk Tech Labs Inc. stands at the forefront of technological innovation. Our team of expert developers, designers, and AI specialists work collaboratively to create solutions that make a real difference in how businesses operate.",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      delay: 0.3
    },
    {
      title: "Cutting-edge Technology", 
      icon: "/handcloud.jpeg",
      description: "We're passionate about leveraging cutting-edge technology to solve complex problems and drive business growth. Our commitment to innovation, coupled with our deep technical expertise, enables us to deliver solutions that not only meet today's needs but are also prepared for tomorrow's challenges.",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      delay: 0.5
    }
  ], []);

  const aboutFeatures = useMemo(() => [
    {
      title: "AI-Powered Solutions",
      description: "Leveraging machine learning and artificial intelligence to create intelligent, adaptive applications.",
      icon: "🤖"
    },
    {
      title: "Scalable Architecture",
      description: "Building robust, scalable systems that grow with your business needs and handle increasing demands.",
      icon: "📈"
    },
    {
      title: "Modern Tech Stack",
      description: "Using the latest technologies including React, Node.js, Python, and cloud-native solutions.",
      icon: "💻"
    },
    {
      title: "Agile Development",
      description: "Fast, iterative development process ensuring quick delivery and continuous improvement.",
      icon: "⚡"
    }
  ], []);

  // Memoized contact data
  const contactData = useMemo(() => [
    { 
      title: "Location", 
      value: "Toronto, Canada", 
      icon: "📍",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    { 
      title: "Email", 
      value: "contact@haydenbulktech.com", 
      icon: "✉️",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    { 
      title: "Response Time", 
      value: "< 24 hours", 
      icon: "⚡",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    }
  ], []);

  // Memoized scroll to contact handler
  const scrollToContact = useCallback(() => {
    document.querySelector('.contact-section')?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  }, []);

  return (
    <div className="home-container" ref={containerRef} role="main" aria-label="Hayden Bulk Tech Labs Homepage">
      {/* Global geometric background */}
      {!isMobile && <GeometricBackground />}
      
      {/* Skip to main content link for accessibility */}
      
      <section className="hero-section" aria-labelledby="hero-heading" id="main-content">
        {!isMobile && <ParticleSystem count={15} />}
        
        {/* Interactive cursor follower */}
        {!isMobile && (
        <motion.div 
            className="cursor-follower"
            animate={{
              x: mousePosition.x * 20,
              y: mousePosition.y * 20,
            }}
            transition={{ type: "spring", damping: 30, stiffness: 200 }}
          />
        )}
        
        <motion.div 
          className="hero-content"
          style={{
            y: heroY,
            opacity: heroOpacity,
            scale: heroScale,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          {/* Morphing logo with advanced animations and complex geometric background */}
          <div className="hero-logo-wrapper">
            {/* Complex geometric design behind logo */}
            {/* {!isMobile && <ComplexGeometricDesign />} */}
            <motion.div className="hero-logo-container">
              <img 
            src="/hayden-bulk-transparent.png"
            alt="Hayden Bulk Tech Labs Logo"
              />
            </motion.div>
          </div>
          
          {/* Animated text with staggered reveal */}
          <motion.h1
            id="hero-heading"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
          >
              <motion.span
                key={'Hayden Bulk Tech Labs'}
                initial={{ opacity: 0, y: 20, rotateX: -90 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                // transition={{
                //   delay: 0.7 + index * 0.1,
                //   duration: 0.6,
                //   ease: "easeOut"
                // }}
                className="hero-word"
              >
                {"Hayden Bulk Tech Labs"}{}
              </motion.span>
          </motion.h1>
          
          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <motion.span
              animate={{
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="gradient-text"
            >
              Innovating Tomorrow's Technology Today
            </motion.span>
          </motion.p>
          
          <motion.p 
            className="hero-description"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
          >
            At Hayden Bulk Tech Labs, we're pioneering the future of technology through innovative AI-driven solutions. 
            Our expertise lies in creating cutting-edge applications that transform how businesses operate and interact 
            with their customers. By leveraging advanced artificial intelligence and machine learning technologies, 
            we help companies stay ahead in today's rapidly evolving digital landscape.
          </motion.p>
          
          {/* Advanced CTA button with morphing effects */}
          <motion.button
            className="hero-cta"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.8, duration: 0.6 }}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(37, 99, 235, 0.3)",
            }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToContact}
          >
            <motion.span
              animate={{
                x: [0, 5, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Start Your Project →
            </motion.span>
          </motion.button>
        </motion.div>
        
        {/* Dynamic background with mathematical patterns - reduced for performance */}
        {!isMobile && (
        <div className="hero-background-dynamic">
          {Array.from({ length: 4 }, (_, i) => (
            <div
              key={i}
              className="hero-bg-element"
              style={{
                left: `${20 + (i % 2) * 40}%`,
                top: `${25 + Math.floor(i / 2) * 40}%`,
              }}
            />
          ))}
        </div>
        )}
        
        <motion.div 
          className="hero-background-image"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.05, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
        >
          <img src="/futuremonitor.jpeg" alt="Future Technology" loading="lazy" />
        </motion.div>
      </section>

      <section className="services-section">
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          Our Services
        </motion.h2>
        
        <div className="services-grid">
          {servicesData.map((service, index) => (
          <motion.div 
              key={service.title}
              className="service-card-3d"
              initial={{ opacity: 0, y: 100, rotateY: -15 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.8, 
                delay: service.delay,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{
                y: -20,
                rotateY: 5,
                rotateX: 5,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className="service-card-inner">
                <div className="service-icon-3d">
                  <motion.div 
                    className="service-image-container"
                    whileHover={{
                      rotateY: 15,
                      rotateX: 10,
                      scale: 1.1,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <img src={service.image} alt={service.title} loading="lazy" />
                  </motion.div>
                </div>
                
                <motion.h3
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: service.delay + 0.3, duration: 0.5 }}
                >
                  {service.title.split(' ').map((word, wordIndex) => (
                    <motion.span
                      key={`${word}-${wordIndex}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: service.delay + 0.4 + wordIndex * 0.1,
                        duration: 0.4
                      }}
                      className="service-title-word"
                      style={{ marginRight: '0.5rem' }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h3>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: service.delay + 0.6, duration: 0.6 }}
                >
                  {service.description}
                </motion.p>
                
                {/* Holographic border effect */}
                <motion.div
                  className="holographic-border"
                  animate={{
                    opacity: [0.05, 0.2, 0.05],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
            </div>
          </motion.div>
          ))}
        </div>
        
        {/* Section background effects */}
        <motion.div
          className="services-bg-effect"
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.1, 0.25, 0.1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </section>

      <section className="about-section">
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          About Us
        </motion.h2>
        
        {/* Main About Content - Redesigned Layout */}
        <div className="about-content-redesigned">
          {/* Left Column - Main Content Cards */}
          <motion.div 
            className="about-main-content"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {aboutData.map((item, index) => (
            <motion.div 
                key={item.title}
                className="about-card-modern"
                initial={{ opacity: 0, y: 50, rotateY: -20 }}
                whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: true }}
                transition={{ duration: 0.6, delay: item.delay }}
                whileHover={{
                  y: -10,
                  rotateY: 5,
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              >
                {/* Static background - no animation */}
                <div
                  className="about-card-bg"
                  style={{ background: item.gradient }}
                />
                
          
          <motion.div 
                  className="about-icon-modern"
                  whileHover={{
                    rotateY: 20,
                    scale: 1.1,
                  }}
                transition={{ duration: 0.3 }}
                >
                  <img src={item.icon} alt={`${item.title} Icon`} loading="lazy" />
            <div
                    className="icon-glow"
                    style={{ background: item.gradient }}
                  />
                </motion.div>
                
                <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: item.delay + 0.2, duration: 0.5 }}
                >
                  {item.title.split(' ').map((word, wordIndex) => (
                    <motion.span
                      key={word}
                      initial={{ opacity: 0, rotateX: -90 }}
                      whileInView={{ opacity: 1, rotateX: 0 }}
                      transition={{
                        delay: item.delay + 0.3 + wordIndex * 0.1,
                        duration: 0.4
                      }}
                      style={{
                        background: item.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        display: 'inline-block',
                        marginRight: '0.3rem'
                      }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </motion.h3>
                
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: item.delay + 0.4, duration: 0.6 }}
                >
                  {item.description}
                </motion.p>
                
                {/* Interactive progress bar */}
                <motion.div
                  className="about-progress"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ delay: item.delay + 0.6, duration: 1 }}
                  style={{ background: item.gradient }}
                />
            </motion.div>
            ))}
          </motion.div>

          {/* Right Column - Stats and Features */}
          <motion.div 
            className="about-sidebar"
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >

            {/* Features Section */}
            <motion.div 
              className="about-features-container"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <h3>What Sets Us Apart</h3>
              <div className="about-features-list">
                {aboutFeatures.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="feature-item"
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                    whileHover={{
                      x: 10,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <div className="feature-content">
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Tech Stack Visualization */}
            <motion.div 
              className="tech-stack-visual"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              <h3>Technologies We Love</h3>
              <div className="tech-tags">
                {['React Native', 'Node', 'AI/ML', 'Unity', 'AWS', 'Docker', 'TypeScript', 'Mongodb', 'Google Gemini', 'ChatGPT', 'Solana'].map((tech, index) => (
                  <motion.span
                    key={tech}
                    className="tech-tag"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 1.1 + index * 0.05 }}
                    whileHover={{
                      scale: 1.1,
                      rotate: 5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Section background effects */}
        <motion.div
          className="about-bg-pattern"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </section>

      <section className="contact-section">
        <motion.h2
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          Get in Touch
        </motion.h2>
        
        <div className="contact-container">
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Let's Build Something Amazing
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              Have a project in mind? We'd love to hear about it. Send us a message and we'll get back to you as soon as possible.
            </motion.p>
            
            <div className="contact-details">
              {contactData.map((item, index) => (
                <motion.div 
                  key={item.title}
                  className="contact-item-modern"
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  whileHover={{
                    scale: 1.05,
                    y: -5,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div className="contact-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.value}</p>
                </motion.div>
              ))}
              </div>
          </motion.div>
          
          <motion.form 
            ref={formRef}
            className="contact-form-modern"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 50, rotateY: 10 }}
            whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            {/* Animated form background */}
            <motion.div
              className="form-bg-effect"
              animate={{
                background: [
                  "linear-gradient(45deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))",
                  "linear-gradient(45deg, rgba(240, 147, 251, 0.05), rgba(245, 87, 108, 0.05))",
                  "linear-gradient(45deg, rgba(79, 172, 254, 0.05), rgba(0, 242, 254, 0.05))",
                  "linear-gradient(45deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))"
                ],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            <AnimatePresence>
              {submitStatus === 'success' && (
                <motion.div 
                  className="alert alert-success-modern"
                  initial={{ opacity: 0, y: -20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.8 }}
                  transition={{ duration: 0.5, type: "spring" }}
                >
                  <div className="alert-icon">✨</div>
                  <div>
                    <h4>Message Sent!</h4>
                    <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  </div>
                </motion.div>
              )}
              
              {submitStatus === 'error' && (
                <motion.div 
                  className="alert alert-error-modern"
                  initial={{ opacity: 0, y: -20, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.8 }}
                  transition={{ duration: 0.5, type: "spring" }}
                >
                  <div className="alert-icon">⚠️</div>
                  <div>
                    <h4>Error Occurred</h4>
                    <p>There was an issue sending your message. Please try again.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {[
              { name: "name", label: "Name", type: "text", required: true },
              { name: "email", label: "Email", type: "email", required: true },
              { name: "company", label: "Company", type: "text", required: false },
            ].map((field, index) => (
              <motion.div 
                key={field.name}
                className="form-group-modern"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
              >
                <motion.label 
                  htmlFor={field.name}
                  animate={{
                    color: formData[field.name] ? "#667eea" : "#6b7280",
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {field.label} {field.required && <span className="required">*</span>}
                </motion.label>
                
                <motion.div className="input-container">
                  <motion.input
                    type={field.type}
                    id={field.name}
                    name={field.name}
                    value={formData[field.name]}
                onChange={handleChange}
                    className={errors[field.name] ? "error" : ""}
                    whileFocus={{
                      scale: 1.02,
                      boxShadow: "0 0 20px rgba(102, 126, 234, 0.2)",
                    }}
                    transition={{ duration: 0.2 }}
                  />
                  
                  {/* Animated underline */}
                  <motion.div
                    className="input-underline"
                    initial={{ width: 0 }}
                    animate={{
                      width: formData[field.name] ? "100%" : "0%",
                      background: errors[field.name] 
                        ? "linear-gradient(90deg, #ef4444, #f87171)" 
                        : "linear-gradient(90deg, #667eea, #764ba2)",
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  
                </motion.div>
                
                <AnimatePresence>
                  {errors[field.name] && (
                    <motion.span 
                      className="error-message-modern"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      ⚠️ {errors[field.name]}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
            
            {/* Message field with special treatment */}
            <motion.div 
              className="form-group-modern message-group"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <motion.label 
                htmlFor="message"
                animate={{
                  color: formData.message ? "#667eea" : "#6b7280",
                }}
              >
                Message <span className="required">*</span>
              </motion.label>
              
              <motion.div className="textarea-container">
                <motion.textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className={errors.message ? "error" : ""}
                  whileFocus={{
                    scale: 1.02,
                    boxShadow: "0 0 20px rgba(102, 126, 234, 0.2)",
                  }}
                  transition={{ duration: 0.2 }}
                />
                
                {/* Character counter */}
                <motion.div
                  className="char-counter"
                  animate={{
                    color: formData.message.length > 500 ? "#ef4444" : "#6b7280",
                  }}
                >
                  {formData.message.length}/500
                </motion.div>
                
                <motion.div
                  className="textarea-underline"
                  initial={{ width: 0 }}
                  animate={{
                    width: formData.message ? "100%" : "0%",
                    background: errors.message 
                      ? "linear-gradient(90deg, #ef4444, #f87171)" 
                      : "linear-gradient(90deg, #667eea, #764ba2)",
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
              
              <AnimatePresence>
                {errors.message && (
                  <motion.span 
                    className="error-message-modern"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    ⚠️ {errors.message}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
            
            {/* Advanced submit button */}
            <motion.button 
              type="submit" 
              className={`submit-button-modern ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 20px 40px rgba(102, 126, 234, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                className="button-bg"
                style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
              />
              
              <span className="button-content">
              {isSubmitting ? (
                  <motion.div className="loading-content">
                    <motion.div
                      className="spinner-modern"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    <span>Sending...</span>
                  </motion.div>
                ) : (
                  <motion.div className="send-content">
                    <span>Send Message</span>
                    <motion.span
                      className="arrow"
                      animate={{
                        x: [0, 5, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      →
                    </motion.span>
                  </motion.div>
                )}
              </span>
              
            </motion.button>
          </motion.form>
        </div>
        
        {/* Simplified background - removed heavy animations */}
        <div className="contact-background-dynamic">
          <img
            src="/computercoffee.jpeg"
            alt="Contact Background"
            loading="lazy"
            style={{ filter: 'brightness(0.9)' }}
          />
        </div>
        
      </section>
    </div>
  );
}